import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Supplier, SupplierPayload, SupplierStatus } from '../features/suppliers/types';
import { filterDigitsOnly, filterPhoneInput, withInputFilter } from '../lib/inputFilters';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const PHONE_REGEX = /^\+?[0-9]+$/;
const TAX_ID_REGEX = /^[0-9]+$/;
/** CUIT argentino: 11 dígitos (sin guiones). */
const SUPPLIER_TAX_ID_MAX_LENGTH = 11;

const schema = z.object({
  legalName: z.string().min(1, 'Requerido').max(255),
  taxId: z
    .string()
    .trim()
    .max(
      SUPPLIER_TAX_ID_MAX_LENGTH,
      `CUIT/RUT: máximo ${SUPPLIER_TAX_ID_MAX_LENGTH} dígitos`,
    )
    .optional()
    .refine((v) => !v || TAX_ID_REGEX.test(v), 'CUIT debe contener solo números'),
  category: z.string().trim().max(120).optional(),
  status: z.enum(['apto', 'no_apto', 'suspendido', 'baja']),
  contactName: z.string().trim().max(120).optional(),
  contactEmail: z
    .string()
    .trim()
    .max(255)
    .optional()
    .refine((v) => !v || z.string().email().safeParse(v).success, 'Email inválido'),
  contactPhone: z
    .string()
    .trim()
    .max(50)
    .optional()
    .refine(
      (v) => !v || PHONE_REGEX.test(v),
      'Teléfono inválido: use solo números y opcionalmente + al inicio',
    ),
  address: z.string().trim().max(1000).optional(),
  notes: z.string().trim().max(5000).optional(),
  reevaluationFrequencyMonths: z
    .union([z.coerce.number().int().min(1).max(120), z.literal('')])
    .optional()
    .transform((v) => (v === '' || v === undefined ? undefined : v)),
  nextReevaluationAt: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

function emptyToNull(v: string | undefined | null): string | null {
  return v && v.length > 0 ? v : null;
}

const STATUSES: { value: SupplierStatus; label: string }[] = [
  { value: 'apto', label: 'Apto' },
  { value: 'no_apto', label: 'No apto' },
  { value: 'suspendido', label: 'Suspendido' },
  { value: 'baja', label: 'Baja' },
];

interface Props {
  initial?: Supplier | null;
  loading?: boolean;
  onSubmit: (payload: SupplierPayload) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

export function SupplierForm({ initial, loading, onSubmit, onCancel, readOnly }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(initial),
  });

  useEffect(() => {
    reset(toFormValues(initial));
  }, [initial, reset]);

  const submit = handleSubmit((values) => {
    onSubmit({
      legalName: values.legalName,
      taxId: emptyToNull(values.taxId),
      category: emptyToNull(values.category),
      status: values.status,
      contactName: emptyToNull(values.contactName),
      contactEmail: emptyToNull(values.contactEmail),
      contactPhone: emptyToNull(values.contactPhone),
      address: emptyToNull(values.address),
      notes: emptyToNull(values.notes),
      reevaluationFrequencyMonths: values.reevaluationFrequencyMonths ?? null,
      nextReevaluationAt: values.nextReevaluationAt
        ? new Date(values.nextReevaluationAt).toISOString()
        : null,
    });
  });

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Razón social *" error={errors.legalName?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: Acme S.A."
          {...register('legalName')}
        />
      </Field>
      <Field label="CUIT / RUT" error={errors.taxId?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          inputMode="numeric"
          maxLength={SUPPLIER_TAX_ID_MAX_LENGTH}
          placeholder="11 dígitos"
          {...withInputFilter(
            register('taxId'),
            (v) => filterDigitsOnly(v, SUPPLIER_TAX_ID_MAX_LENGTH),
          )}
        />
      </Field>
      <Field label="Categoría" error={errors.category?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: Servicios, insumos…"
          {...register('category')}
        />
      </Field>
      <Field label="Estado" error={errors.status?.message}>
        <select
          className="border rounded-md px-3 py-2 w-full"
          disabled={readOnly}
          {...register('status')}
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Frecuencia de reevaluación (meses)"
        error={errors.reevaluationFrequencyMonths?.message}
      >
        <input
          type="number"
          min={1}
          max={120}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Ej: 12"
          {...register('reevaluationFrequencyMonths')}
        />
      </Field>
      <Field label="Próxima reevaluación" error={errors.nextReevaluationAt?.message}>
        <input
          type="date"
          className="border rounded-md px-3 py-2 w-full"
          disabled={readOnly}
          {...register('nextReevaluationAt')}
        />
      </Field>
      <Field label="Contacto — Nombre" error={errors.contactName?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Nombre del contacto"
          {...register('contactName')}
        />
      </Field>
      <Field label="Contacto — Email" error={errors.contactEmail?.message}>
        <input
          type="email"
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="contacto@empresa.com"
          {...register('contactEmail')}
        />
      </Field>
      <Field label="Contacto — Teléfono" error={errors.contactPhone?.message}>
        <input
          type="tel"
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          inputMode="tel"
          placeholder="+5491112345678"
          {...withInputFilter(register('contactPhone'), filterPhoneInput)}
        />
      </Field>
      <Field label="Dirección" error={errors.address?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Calle, número, ciudad"
          {...register('address')}
        />
      </Field>
      <Field label="Notas" error={errors.notes?.message} colSpan={2}>
        <textarea
          rows={3}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          disabled={readOnly}
          placeholder="Observaciones internas"
          {...register('notes')}
        />
      </Field>

      <RequiredFieldsLegend className="md:col-span-2" />
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
          onClick={onCancel}
        >
          Cancelar
        </button>
        {!readOnly && (
          <button
            type="submit"
            disabled={loading}
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
          </button>
        )}
      </div>
    </form>
  );
}

function toFormValues(initial?: Supplier | null): FormValues {
  return {
    legalName: initial?.legalName ?? '',
    taxId: initial?.taxId ?? '',
    category: initial?.category ?? '',
    status: initial?.status ?? 'apto',
    reevaluationFrequencyMonths: initial?.reevaluationFrequencyMonths ?? undefined,
    nextReevaluationAt: initial?.nextReevaluationAt?.slice(0, 10) ?? '',
    contactName: initial?.contactName ?? '',
    contactEmail: initial?.contactEmail ?? '',
    contactPhone: initial?.contactPhone ?? '',
    address: initial?.address ?? '',
    notes: initial?.notes ?? '',
  };
}

function Field({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
